import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { formatCurrency, formatPercent } from "../engine/calc2026";

export const SharedResult = () => {
  const { t } = useTranslation();

  const data = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const num = (key) => parseFloat(params.get(key)) || 0;
    const base = num("base");
    const ipc = num("ipc");
    const taxesMuni = num("taxes_muni");
    const taxesScol = num("taxes_scol");
    const assurancesParam = params.get("assurances");
    const assurancesUnknown =
      assurancesParam === "inconnue" || params.get("manque_assurance") === "true";
    const assurances = assurancesUnknown ? 0 : num("assurances");
    const travaux = num("travaux");
    const prorataLabel = params.get("prorata") || "";
    const prorataEstimated = params.get("prorata_estime") === "true";

    const totalIncrease = ipc + taxesMuni + taxesScol + assurances + travaux;
    const estimatedRent = base + totalIncrease;

    return {
      base,
      ipc,
      taxesMuni,
      taxesScol,
      assurances,
      assurancesUnknown,
      travaux,
      prorataLabel,
      prorataEstimated,
      totalIncrease,
      estimatedRent,
    };
  }, []);

  return (
    <div className="w-full max-w-3xl mx-auto mt-4 brand-shell p-6 text-gray-800">
      <div className="text-center mb-4">
        <p className="uppercase tracking-wide text-xs text-gray-500 mb-1">
          {t("sharedResult.header", "Estimation partagée")}
        </p>
        <p className="text-4xl font-bold text-gray-900">{formatCurrency(data.estimatedRent)}</p>
        <p className="text-lg text-gray-600">
          +{formatCurrency(data.totalIncrease)}{" "}
          {data.base > 0 ? `(${formatPercent(data.totalIncrease / data.base)})` : ""}
        </p>
      </div>

      <div className="border-t border-gray-200 my-4" />

      <h3 className="font-semibold text-gray-900 mb-3">
        {t("sharedResult.details", "Détail partagé")}
      </h3>

      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span>{t("2026.summary.previousRent", "Loyer avant")}</span>
          <span className="font-medium">{formatCurrency(data.base)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-emerald-700">+ IPC</span>
          <span className="font-medium text-emerald-700">{formatCurrency(data.ipc)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-emerald-700">+ {t("2026.breakdown.municipalTax", "Taxes municipales")}</span>
          <span className="font-medium text-emerald-700">{formatCurrency(data.taxesMuni)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-emerald-700">+ {t("2026.breakdown.schoolTax", "Taxes scolaires")}</span>
          <span className="font-medium text-emerald-700">{formatCurrency(data.taxesScol)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-emerald-700">+ {t("2026.summary.insurance", "Assurances")}</span>
          <span className="font-medium text-emerald-700">
            {data.assurancesUnknown ? t("sharedResult.unknown", "?") : formatCurrency(data.assurances)}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-emerald-700">+ {t("2026.summary.works", "Travaux")}</span>
          <span className="font-medium text-emerald-700">{formatCurrency(data.travaux)}</span>
        </div>
        <div className="border-t pt-2 mt-2 flex justify-between font-bold text-lg">
          <span>{t("2026.summary.total", "Total estimé")}</span>
          <span className="text-emerald-700">{formatCurrency(data.estimatedRent)}</span>
        </div>
      </div>

      {(data.prorataEstimated || data.assurancesUnknown) && (
        <div className="brand-card-soft p-4 mt-6">
          <h4 className="font-semibold text-gray-900 mb-2">
            {t("sharedResult.notes", "Notes")}
          </h4>
          <ul className="text-sm text-gray-800 list-disc list-inside space-y-1">
            {data.prorataEstimated && (
              <li>
                {t("sharedResult.prorata", "Répartition estimée")}: {data.prorataLabel || t("sharedResult.unknown", "?")}
              </li>
            )}
            {data.assurancesUnknown && (
              <li>{t("sharedResult.insuranceMissing", "Assurances inconnues (montant à confirmer).")}</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};
